// Monitor — the running system: jobs across every tenant, and the audit trail.
//
// Two tabs rather than two screens because they answer one question in
// sequence: something failed (jobs), and here is what happened around it
// (events). Filters survive the tab switch for the same reason.

import React from 'react';
import { api, errorText } from '../api';
import { useAsync } from '../useAsync';
import { navigate } from '../router';
import { ago, duration, jobTone, num, stamp } from '../format';
import Sparkline from '../ui/Sparkline';
import { useToast } from '../ui/Toast';
import {
  Button, Card, Chips, Empty, ErrorState, KV, PageHead, Pill, RefreshButton,
  Sheet, Skeleton,
} from '../ui/primitives';

const JOB_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'failed', label: 'Failed' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'interrupted', label: 'Interrupted' },
];

const OPEN_STATES = ['queued', 'running'];

function JobSheet({ job, onClose, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  const cancellable = OPEN_STATES.includes(job.status);

  return (
    <Sheet title={job.kind} subtitle={job.job_id} onClose={onClose}
           footer={cancellable ? (
             <Button variant="danger" loading={busy} onClick={async () => {
               setBusy(true);
               try {
                 await api.cancelJob(job.job_id);
                 toast.ok('Cancellation requested.');
                 onChanged();
                 onClose();
               } catch (e) {
                 toast.error(errorText(e));
               } finally { setBusy(false); }
             }}>Request cancellation</Button>
           ) : null}>
      <div className="stack">
        <div className="row row-wrap" style={{ gap: 6 }}>
          <Pill tone={jobTone(job.status)}>{job.status}</Pill>
          {job.live && <Pill tone="info">live</Pill>}
          {job.cancel_requested && <Pill tone="warn">cancel requested</Pill>}
        </div>

        {job.error && (
          <div className="note danger">
            <div className="note-title">Error</div>
            <pre className="payload" style={{ marginTop: 6 }}>{job.error}</pre>
          </div>
        )}

        <KV items={[
          ['Org', job.org_name || job.org_id || '—'],
          ['User', job.user_email || job.user_id || '—'],
          ['Created', stamp(job.created_at)],
          ['Started', job.started_at ? stamp(job.started_at) : null],
          ['Finished', job.finished_at ? stamp(job.finished_at) : null],
          ['Duration', job.duration_ms != null ? duration(job.duration_ms) : null],
          ['Job id', <span key="j" className="mono" style={{ fontSize: 11 }}>{job.job_id}</span>],
        ]} />

        {job.progress && (
          <div>
            <h2 style={{ marginBottom: 6 }}>Progress</h2>
            <pre className="payload">{JSON.stringify(job.progress, null, 2)}</pre>
          </div>
        )}

        <Button small onClick={() => {
          onClose();
          navigate(`/monitor?tab=events&entity=${encodeURIComponent(job.job_id)}`);
        }}>
          Events for this job
        </Button>
      </div>
    </Sheet>
  );
}

function JobsTab({ orgId, initialStatus, initialOpen }) {
  const [filter, setFilter] = React.useState(
    initialOpen ? 'open' : (initialStatus || 'all'));
  const [open, setOpen] = React.useState(null);

  const { data, error, loading, at, reload } = useAsync(
    () => api.jobs({
      orgId,
      openOnly: filter === 'open',
      status: filter === 'open' || filter === 'all' ? null : filter,
      limit: 60,
    }), [orgId, filter]);

  return (
    <>
      <div className="row" style={{ marginBottom: 12, gap: 9 }}>
        <Chips options={JOB_FILTERS} value={filter} onChange={setFilter} />
        <div className="spacer" />
        <RefreshButton onClick={reload} loading={loading} at={at} />
      </div>

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {!data && loading && <Skeleton rows={5} />}

      {data && data.items.length === 0 && (
        <Empty title="No jobs">Nothing matches this filter.</Empty>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="card">
            <div className="list">
              {data.items.map((j) => (
                <button key={j.job_id} className="list-row" type="button"
                        onClick={() => setOpen(j)}>
                  <Pill tone={jobTone(j.status)}>{j.status}</Pill>
                  <div className="col" style={{ flex: 1 }}>
                    <div className="row" style={{ gap: 6 }}>
                      <span className="truncate" style={{ fontSize: 13 }}>{j.kind}</span>
                      {j.live && <span className="dot" style={{ color: 'var(--info)' }} />}
                    </div>
                    <div className="dim truncate" style={{ fontSize: 11 }}>
                      {j.org_name || '—'} · {j.user_email || 'anonymous'} · {ago(j.created_at)}
                    </div>
                  </div>
                  {j.duration_ms != null && (
                    <span className="num dim" style={{ fontSize: 11 }}>{duration(j.duration_ms)}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 8, textAlign: 'center' }}>
            showing {data.items.length} of {num(data.total)}
          </div>
        </>
      )}

      {open && <JobSheet job={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </>
  );
}

function EventsTab({ orgId, entityId }) {
  const [type, setType] = React.useState('all');
  const [open, setOpen] = React.useState(null);
  const facets = useAsync(() => api.eventFacets(7), []);

  const { data, error, loading, at, reload } = useAsync(
    () => api.events({
      orgId,
      entityId: entityId || null,
      eventType: type === 'all' ? null : type,
      limit: 80,
    }), [orgId, entityId, type]);

  // Families ("auth.*") rather than 30 individual chips: the type list grows
  // with every feature PR, and a filter strip you have to scroll for ten
  // seconds is not a filter.
  const families = React.useMemo(() => {
    const names = new Set((facets.data?.types || []).map((t) => t.event_type.split('.')[0]));
    return [{ value: 'all', label: 'All' },
            ...[...names].sort().map((n) => ({ value: `${n}.*`, label: n }))];
  }, [facets.data]);

  return (
    <>
      <div className="row" style={{ marginBottom: 12, gap: 9 }}>
        <Chips options={families} value={type} onChange={setType} />
        <div className="spacer" />
        <RefreshButton onClick={reload} loading={loading} at={at} />
      </div>

      {entityId && (
        <div className="note" style={{ marginBottom: 12 }}>
          Filtered to entity <span className="mono">{entityId}</span>{' '}
          <button className="btn ghost small" onClick={() => navigate('/monitor?tab=events')}>
            clear
          </button>
        </div>
      )}

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {!data && loading && <Skeleton rows={6} />}

      {data && data.items.length === 0 && (
        <Empty title="No events">Nothing recorded for this filter.</Empty>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="card">
            <div className="list">
              {data.items.map((e) => (
                <button key={e.id} className="list-row" type="button"
                        onClick={() => setOpen(e)}>
                  <div className="col" style={{ flex: 1 }}>
                    <span className="mono truncate" style={{ fontSize: 12 }}>{e.event_type}</span>
                    <span className="dim truncate" style={{ fontSize: 11 }}>
                      {e.actor_email || 'system'}
                      {e.org_name ? ` · ${e.org_name}` : ''} · {ago(e.ts)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 8, textAlign: 'center' }}>
            showing {data.items.length} of {num(data.total)}
          </div>
        </>
      )}

      {open && (
        <Sheet title={open.event_type} subtitle={stamp(open.ts)} onClose={() => setOpen(null)}>
          <div className="stack">
            <KV items={[
              ['When', stamp(open.ts)],
              ['Actor', open.actor_email || 'system'],
              ['Org', open.org_name || '—'],
              ['Entity', open.entity_type ? `${open.entity_type} ${open.entity_id || ''}` : null],
              ['Request id', open.request_id
                ? <span key="r" className="mono" style={{ fontSize: 11 }}>{open.request_id}</span>
                : null],
            ]} />
            {open.payload && (
              <div>
                <h2 style={{ marginBottom: 6 }}>Payload</h2>
                <pre className="payload">{JSON.stringify(open.payload, null, 2)}</pre>
              </div>
            )}
            <div className="dim" style={{ fontSize: 11.5 }}>
              Events are append-only. Nothing in this app can edit or delete one.
            </div>
          </div>
        </Sheet>
      )}
    </>
  );
}

export default function Monitor({ query }) {
  const [tab, setTab] = React.useState(query.tab === 'events' ? 'events' : 'jobs');
  const orgId = query.org || null;
  const act = useAsync(() => api.activity(14, orgId), [orgId]);
  const org = useAsync(() => api.org(orgId), [orgId], { skip: !orgId });

  return (
    <>
      <PageHead title="Monitor"
                sub={orgId
                  ? <span className="row" style={{ gap: 7 }}>
                      <span className="dim">scoped to {org.data?.name || orgId}</span>
                      <button className="btn ghost small" onClick={() => navigate('/monitor')}>
                        clear
                      </button>
                    </span>
                  : 'Jobs and audit trail across every tenant'} />

      {act.data && (
        <Card title="14 days">
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <div className="col" style={{ gap: 4 }}>
              <span className="stat-label">Jobs</span>
              <Sparkline buckets={act.data.buckets} valueKey="jobs"
                         alertKey="jobs_failed" label="jobs per day" />
            </div>
            <div className="col" style={{ gap: 4 }}>
              <span className="stat-label">Logins</span>
              <Sparkline buckets={act.data.buckets} valueKey="logins"
                         label="logins per day" />
            </div>
          </div>
        </Card>
      )}

      <div className="chips" style={{ margin: '16px 0 12px' }}>
        <button className="chip" aria-pressed={tab === 'jobs'} onClick={() => setTab('jobs')}>Jobs</button>
        <button className="chip" aria-pressed={tab === 'events'} onClick={() => setTab('events')}>Events</button>
      </div>

      {tab === 'jobs'
        ? <JobsTab orgId={orgId} initialStatus={query.status} initialOpen={query.open === '1'} />
        : <EventsTab orgId={orgId} entityId={query.entity} />}
    </>
  );
}
